import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, FileText, Video, Download, Mail, MessageSquare, Users, Plus, BarChart3, Image, GraduationCap, Activity, Megaphone, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import BlogManager from '../components/admin/BlogManager';
import VideoManager from '../components/admin/VideoManager';
import ResourceManager from '../components/admin/ResourceManager';
import SubmissionsView from '../components/admin/SubmissionsView';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import CommentManager from '../components/admin/CommentManager';
import BannerManager from '../components/admin/BannerManager';
import EmailTemplatePreview from '../components/admin/EmailTemplatePreview';
import EmailActivityLog from '../components/admin/EmailActivityLog';
import EmailCourseStats from '../components/admin/EmailCourseStats';
import QuizSponsorManager from '../components/admin/QuizSponsorManager';
import SponsorshipManager from '../components/admin/SponsorshipManager';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    blogPosts: 0,
    videos: 0,
    resources: 0,
    newsletters: 0,
    contacts: 0
  });

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-sm text-gray-400">Welcome back, {user}</p>
            </div>
            <Button
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Blog Posts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-orange-400" />
                <span className="text-2xl font-bold text-white">{stats.blogPosts}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Videos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Video className="h-4 w-4 text-orange-400" />
                <span className="text-2xl font-bold text-white">{stats.videos}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Resources</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Download className="h-4 w-4 text-orange-400" />
                <span className="text-2xl font-bold text-white">{stats.resources}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Subscribers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-orange-400" />
                <span className="text-2xl font-bold text-white">{stats.newsletters}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Contacts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-4 w-4 text-orange-400" />
                <span className="text-2xl font-bold text-white">{stats.contacts}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Content Management */}
        <Tabs defaultValue="analytics" className="space-y-4">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <BarChart3 className="mr-2 h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <FileText className="mr-2 h-4 w-4" />
              Blog Posts
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Video className="mr-2 h-4 w-4" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="resources" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Download className="mr-2 h-4 w-4" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="submissions" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Users className="mr-2 h-4 w-4" />
              Submissions
            </TabsTrigger>
            <TabsTrigger value="comments" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <MessageSquare className="mr-2 h-4 w-4" />
              Comments
            </TabsTrigger>
            <TabsTrigger value="banners" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Image className="mr-2 h-4 w-4" />
              Banner Ads
            </TabsTrigger>
            <TabsTrigger value="email-logs" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Activity className="mr-2 h-4 w-4" />
              Email Activity
            </TabsTrigger>
            <TabsTrigger value="course-stats" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <GraduationCap className="mr-2 h-4 w-4" />
              Course Stats
            </TabsTrigger>
            <TabsTrigger value="quiz-sponsors" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Megaphone className="mr-2 h-4 w-4" />
              Quiz Sponsors
            </TabsTrigger>
            <TabsTrigger value="sponsorship" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <DollarSign className="mr-2 h-4 w-4" />
              Sponsorship
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Mail className="mr-2 h-4 w-4" />
              Templates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="blog">
            <BlogManager onStatsUpdate={(count) => setStats({...stats, blogPosts: count})} />
          </TabsContent>

          <TabsContent value="videos">
            <VideoManager onStatsUpdate={(count) => setStats({...stats, videos: count})} />
          </TabsContent>

          <TabsContent value="resources">
            <ResourceManager onStatsUpdate={(count) => setStats({...stats, resources: count})} />
          </TabsContent>

          <TabsContent value="submissions">
            <SubmissionsView onStatsUpdate={(newsletter, contact) => setStats({...stats, newsletters: newsletter, contacts: contact})} />
          </TabsContent>

          <TabsContent value="comments">
            <CommentManager />
          </TabsContent>

          <TabsContent value="banners">
            <BannerManager />
          </TabsContent>

          <TabsContent value="email-logs">
            <EmailActivityLog />
          </TabsContent>

          <TabsContent value="course-stats">
            <EmailCourseStats />
          </TabsContent>

          <TabsContent value="quiz-sponsors">
            <QuizSponsorManager />
          </TabsContent>

          <TabsContent value="sponsorship">
            <SponsorshipManager />
          </TabsContent>

          <TabsContent value="emails">
            <EmailTemplatePreview />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
